import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

import { cn } from '@/lib/cn';

// FASE-B (maquete scr-funil): o header de coluna deixou de ser um Badge-pílula
// e virou a anatomia "dot colorido 8px + nome da fase (Inter 600 14)". As cores
// do dot seguem o variant do stage (tokens AA); neutral = dot discreto (o
// equivalente do --line-strong da maquete via ink com alpha, sem cor crua).
const DOT_CLASSES: Record<NonNullable<EditableColumnHeaderProps['variant']>, string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-red dark:bg-red-glow',
  info: 'bg-info',
  neutral: 'bg-ink-3/40 dark:bg-ink-dark-3/50',
};

interface EditableColumnHeaderProps {
  /** Slug imutável da coluna — só usado pra identificação semântica, não exibido. */
  slug: string;
  /** Label atual (já resolvido pelo useColumnLabels — pode ser custom ou default). */
  label: string;
  /** Variant do Badge — coerente com o stage info. */
  variant?: 'success' | 'warning' | 'danger' | 'neutral' | 'info';
  /** Se true (admin), click no label entra em modo edição. Se false, label é texto puro. */
  canEdit: boolean;
  /** Chamado ao confirmar edição. Vazio = chamador deve "resetar" pra default. */
  onChange: (novoLabel: string) => void;
}

/**
 * Header de coluna de kanban — admin edita label inline; non-admin vê só texto.
 *
 * UX:
 *   - canEdit=false: renderiza só o Badge.
 *   - canEdit=true: Badge clicável (cursor-pointer + title). Click entra em modo edição
 *     com input inline focado e selecionado. Enter ou blur salva; Escape cancela.
 *
 * Persistência é responsabilidade do chamador (useColumnLabels). Esse componente é
 * stateless quanto à fonte do label — só dispara onChange quando o usuário confirma.
 *
 * Parte do padrão Kanban IGM (memória project_padrao_kanban_igm.md) — reusado em
 * workflows futuros via mesma API.
 */
export function EditableColumnHeader({
  slug,
  label,
  variant = 'neutral',
  canEdit,
  onChange,
}: EditableColumnHeaderProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sincroniza draft com label externo quando não está editando (label mudou via reset, etc)
  useEffect(() => {
    if (!editing) setDraft(label);
  }, [label, editing]);

  // Foca + seleciona ao entrar em modo edição
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  function commit() {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed !== label) {
      onChange(trimmed);
    }
  }

  function cancel() {
    setDraft(label);
    setEditing(false);
  }

  function handleKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        data-testid={`column-label-input-${slug}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
        maxLength={40}
        className="text-xs font-medium px-2 py-0.5 rounded-pill border border-red bg-paper-surface dark:bg-paper-dark-surface text-ink-1 dark:text-ink-dark-1 focus:outline-none focus:ring-2 focus:ring-red-soft dark:focus:ring-red-soft-dark min-w-[120px] max-w-[200px]"
      />
    );
  }

  const rotulo = (
    <span className="flex items-center gap-2 min-w-0">
      <span className={cn('w-2 h-2 rounded-full shrink-0', DOT_CLASSES[variant])} aria-hidden="true" />
      {/* L5 (LAYOUT-CANONICO): nome da fase tem de ser lido de relance — era
          14px, o mesmo peso dos rótulos de apoio. Agora é título de seção
          (16px); contagem e soma seguem em 12px, como apoio. */}
      {/* C-4 (AUDITORIA-COMERCIAL): o nome da fase corta na borda do quadro
          ("Follo…") sem como ler o resto. `title` nativo resolve sem custo. */}
      <span className="text-base font-semibold text-ink-1 dark:text-ink-dark-1 truncate" title={label}>
        {label}
      </span>
    </span>
  );

  if (!canEdit) {
    return rotulo;
  }

  return (
    <button
      type="button"
      data-testid={`column-label-button-${slug}`}
      onClick={() => setEditing(true)}
      title="Click pra renomear (admin)"
      className="cursor-pointer hover:opacity-80 transition-opacity min-w-0"
    >
      {rotulo}
    </button>
  );
}
